import { motion } from "framer-motion";
import {
  Server,
  Database,
  Briefcase,
  Monitor,
  BadgeCheck,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { scrollToId } from "./Nav";
import { useLang } from "@/i18n";

const INFRA_IMG =
  "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxpdCUyMGluZnJhc3RydWN0dXJlJTIwc2VydmVyJTIwcm9vbXxlbnwwfHx8fDE3ODcyNTA0NTV8MA&ixlib=rb-4.1.0&q=85";

const trackMeta = [
  { icon: Server, span: "md:col-span-7", accent: "text-neon-teal", image: INFRA_IMG },
  { icon: Database, span: "md:col-span-5", accent: "text-neon-pink" },
  { icon: Briefcase, span: "md:col-span-4", accent: "text-neon-green" },
  { icon: Monitor, span: "md:col-span-4", accent: "text-neon-teal" },
  { icon: BadgeCheck, span: "md:col-span-4", accent: "text-neon-pink" },
  { icon: ShieldCheck, span: "md:col-span-12", accent: "text-neon-green" },
];

export default function Services() {
  const { t } = useLang();
  const trackTexts = t("services.tracks");
  return (
    <section
      id="services"
      data-testid="services-section"
      className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-36"
    >
      <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-neon-teal"
            data-testid="services-eyebrow"
          >
            {t("services.eyebrow")}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-4xl font-medium tracking-tight text-[#141210] md:text-6xl"
            data-testid="services-heading"
          >
            {t("services.headingA")} <span className="text-outline-teal">{t("services.headingB")}</span>
          </motion.h2>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-sm text-base leading-relaxed text-[#6B655F]"
        >
          {t("services.side")}
        </motion.p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {trackMeta.map((meta, i) => {
          const txt = trackTexts[i];
          const Icon = meta.icon;
          return (
          <motion.button
            key={i}
            data-testid={`service-card-${i}`}
            onClick={() => scrollToId("contact")}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.33, 1, 0.68, 1] }}
            className={`group relative overflow-hidden border border-[#E6E2DC] bg-white p-8 text-left backdrop-blur-2xl transition-[border-color,box-shadow] duration-500 hover:border-[#141210] hover:shadow-[inset_0_0_60px_rgba(88,24,32,0.05)] md:p-10 ${meta.span} ${
              meta.span === "md:col-span-12" ? "md:flex md:items-center md:gap-12" : ""
            }`}
          >
            {meta.image && (
              <>
                <img
                  src={meta.image}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover opacity-20 transition-[opacity,transform] duration-700 group-hover:scale-105 group-hover:opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/75 to-white/20" />
              </>
            )}
            <div className="relative">
              <div className="flex items-start justify-between">
                <Icon size={36} strokeWidth={1.6} className={`${meta.accent}`} />
                <ArrowUpRight
                  size={22}
                  className="text-[#8A837B] transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#141210]"
                />
              </div>
              <h3 className="mt-8 font-display text-2xl font-semibold tracking-tight text-[#141210] md:text-3xl">
                {txt.title}
              </h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-[#6B655F] md:text-base">
                {txt.desc}
              </p>
            </div>
            <span
              className="text-outline pointer-events-none absolute -bottom-4 right-4 font-display text-7xl font-semibold opacity-40"
              aria-hidden="true"
            >
              {String(i + 1).padStart(2, "0")}
            </span>
          </motion.button>
          );
        })}
      </div>
    </section>
  );
}
